import { UserPlusIcon } from "@heroicons/react/24/outline";
import { FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { AssignmentEntityType } from "@/api/rest/models/account";
import { HouseholdData } from "@/api/rest/models/household";
import { updateWarrantyEndDate } from "@/api/rest/services/householdService";
import { Banner } from "@/components/common/Banner";
import ManagedPopup from "@/components/common/ManagedPopup.tsx";
import UserManagement from "@/components/common/UserManagement";
import { sendToSentry } from "@/utils/sentry";

export default function HouseholdSettingsPopup({
  householdData,
  updateHousehold,
  isAdmin = false,
}: {
  householdData: HouseholdData;
  updateHousehold: () => void;
  isAdmin?: boolean;
}) {
  const [warrantyEndDate, setWarrantyEndDate] = useState<string>(
    householdData.household.warrantyEndDateTimestamp.format("YYYY-MM-DD"),
  );
  const [formStatus, setFormStatus] = useState<{
    message: string;
    success: boolean;
  } | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (formStatus) {
      const timer = setTimeout(() => setFormStatus(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [formStatus]);

  const { t } = useTranslation();

  const submitForm = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFormStatus(null);

    try {
      const isoDate = new Date(warrantyEndDate).toISOString();
      const warrantyApiResponse = await updateWarrantyEndDate(householdData.household.id, isoDate);

      if (warrantyApiResponse.success) {
        setFormStatus({
          success: true,
          message: t("settings.saveSuccess"),
        });
        updateHousehold();
      } else {
        setFormStatus({
          success: false,
          message: t("settings.warrantyUpdateFailed"),
        });
      }
    } catch (e) {
      sendToSentry(e as Error, {
        additionalContext: { context: "HouseholdSettingsPopup.submitForm" },
      });

      setFormStatus({
        success: false,
        message: e instanceof Error ? e.message : t("settings.saveFailed"),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const formattedCurrentWarrantyDate = householdData.household.warrantyEndDateTimestamp.format("DD.MM.YYYY");

  return (
    <ManagedPopup
      contentSize="data"
      trigger={<UserPlusIcon className="w-12 h-12 rounded p-2 hover:bg-gray-300 text-gray-500" />}
      title={t("settings.title") + ": " + householdData.household.title}
    >
      <div className="flex flex-col m-2">
        <UserManagement entityId={householdData.household.id} entityType={AssignmentEntityType.HOUSEHOLD} />
      </div>

      {isAdmin && (
        <form className="flex flex-col mx-4 mt-2 mb-4" onSubmit={submitForm}>
          <span className="text-xl font-bold pb-4 block">
            {t("settings.householdSettings") || "Household settings"}
          </span>

          <div className="flex flex-col mb-4">
            <label className="text-gray-500 mb-2" htmlFor="warrantyEndDate">
              {t("settings.warrantyEndDate") || "Warranty end date"}
            </label>

            <input
              className="border border-gray-300 rounded-md p-2 w-full"
              name="warrantyEndDate"
              type="date"
              value={warrantyEndDate}
              onChange={(e) => setWarrantyEndDate(e.target.value)}
            />

            <p className="text-sm text-gray-500 mt-2">
              {t("settings.currentWarrantyEndDate") || "Current warranty end date"}: {formattedCurrentWarrantyDate}
            </p>
          </div>

          {formStatus && (
            <div className="mb-4">
              <Banner message={formStatus.message} success={formStatus.success} />
            </div>
          )}

          {warrantyEndDate !== householdData.household.warrantyEndDateTimestamp.format("YYYY-MM-DD") && (
            <button
              className="rounded-md p-2 flex-grow bg-dobu-orange text-white hover:bg-dobu-orange-focus disabled:bg-gray-300 disabled:text-gray-500"
              disabled={isSubmitting}
            >
              {isSubmitting ? t("common.saving") : t("settings.save")}
            </button>
          )}
        </form>
      )}
    </ManagedPopup>
  );
}
